import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';

const Plans = () => {
  const { t, i18n } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [isYearly, setIsYearly] = useState(false);

  const plans = [
    { ...t('plans.starter', { returnObjects: true }), id: 'starter' },
    { ...t('plans.pro', { returnObjects: true }), id: 'pro' },
    { ...t('plans.business', { returnObjects: true }), id: 'business' },
  ];

  // 価格文字列から数値を抽出して年額を月割で計算
  const getMonthlyFromYearly = (yearlyPrice: string, isJPY: boolean): string => {
    // 数値を抽出（￥や$、カンマを除去）
    const numericValue = parseFloat(yearlyPrice.replace(/[￥$,]/g, ''));
    const monthlyValue = Math.round(numericValue / 12);
    
    if (isJPY) {
      return `￥${monthlyValue.toLocaleString()}`;
    } else {
      return `$${monthlyValue}`;
    }
  };

  const handleGetStarted = () => {
    window.location.href = '?page=trial-signup';
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <section className="py-32 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.h2 
          className="text-4xl md:text-5xl lg:text-6xl font-black text-center text-gray-900 mb-12 tracking-tight"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          {t('plans.title')}
        </motion.h2>

        <motion.div 
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
        >
          <div className="inline-block bg-white border-l-4 border-blue-500 text-blue-800 rounded-2xl p-6 mb-6 shadow-md">
            <p className="font-bold text-lg"><span className="line-through">{t('plans.trialStrikethrough')}</span> <span>{t('plans.trialNew')}</span></p>
            <p className="text-sm mt-2">{t('plans.trialDesc')}</p>
          </div>
          
          {/* 月額/年額切り替え */}
          <div className="flex items-center justify-center gap-4 mt-6">
            <span className={`text-sm font-semibold ${!isYearly ? 'text-blue-600' : 'text-gray-500'}`}>
              {t('plans.monthly')}
            </span>
            <button
              onClick={() => setIsYearly(!isYearly)}
              className={`relative w-14 h-7 rounded-full transition-colors ${
                isYearly ? 'bg-blue-500' : 'bg-gray-300'
              }`}
            >
              <span
                className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${
                  isYearly ? 'translate-x-7' : 'translate-x-0'
                }`}
              />
            </button>
            <span className={`text-sm font-semibold ${isYearly ? 'text-blue-600' : 'text-gray-500'}`}>
              {t('plans.yearly')}
            </span>
          </div>
        </motion.div>

        {/* プラン詳細を表示 */}
        <motion.div 
          ref={ref}
          className="grid md:grid-cols-3 gap-8 mb-12"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          {plans.map((plan: any, index: number) => (
            <motion.div
              key={index}
              className={`rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all relative ${
                index === 1
                  ? 'bg-gradient-to-br from-cyan-50 via-white to-blue-50 border-4 border-blue-500 scale-110 hover:scale-115 shadow-2xl ring-4 ring-cyan-200 ring-opacity-50'
                  : 'bg-white border-2 border-gray-200 hover:scale-105'
              }`}
              variants={itemVariants}
              whileHover={{ scale: index === 1 ? 1.15 : 1.05, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              <h3 className={`text-2xl font-bold mb-4 ${index === 1 ? 'text-blue-600' : 'text-gray-900'}`}>
                {plan.name}
              </h3>

              {plan.recommended && (
                <div className="mb-4">
                  <span className="inline-block bg-blue-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                    {plan.recommended}
                  </span>
                </div>
              )}

              <div className="text-center mb-6">
                {isYearly ? (
                  <div className="space-y-2">
                    <div className="flex flex-col items-center gap-1">
                      <div className="flex items-baseline justify-center gap-2">
                        <span className="text-3xl font-black line-through text-gray-400">
                          {plan.price}
                        </span>
                        <span className={`text-5xl font-black ${index === 1 ? 'text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500' : 'text-blue-500'}`}>
                          {getMonthlyFromYearly(plan.priceYearly, i18n.language === 'ja')}
                        </span>
                        <span className="text-gray-600 text-lg font-semibold">
                          /{t('plans.perMonth')}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500">
                          {i18n.language === 'ja' ? '年額' : 'Yearly'} {plan.priceYearly}
                        </span>
                        <span className="inline-block bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                          {i18n.language === 'ja' ? '約40%OFF' : '~40% OFF'}
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-baseline justify-center gap-2">
                    <span className={`text-6xl font-black ${index === 1 ? 'text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500' : 'text-blue-500'}`}>
                      {plan.price}
                    </span>
                    <span className="text-gray-600 text-xl font-semibold">
                      /{t('plans.perMonth')}
                    </span>
                  </div>
                )}
              </div>

              {/* プラン詳細（features）を全て表示 */}
              {plan.features && Array.isArray(plan.features) && (
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature: string, featureIndex: number) => (
                    <li key={featureIndex} className="flex items-start gap-2 text-gray-700">
                      <svg className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              )}

              {plan.recommendedDesc && (
                <p className="text-xs text-gray-500 text-center mb-4">{plan.recommendedDesc}</p>
              )}
            </motion.div>
          ))}
        </motion.div>

        {/* 大きな無料で始めるボタン */}
        <motion.div 
          className="text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, delay: 0.4, ease: "easeOut" }}
        >
          <motion.button
            onClick={handleGetStarted}
            className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-8 py-4 md:px-20 md:py-8 rounded-2xl text-xl md:text-3xl font-black hover:from-blue-600 hover:to-cyan-600 transition-all hover:scale-110 shadow-2xl"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {t('plans.cta')}
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default Plans;

